import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, of } from 'rxjs';
import { catchError, distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

interface GenericResponse<T> {
  payload?: T;
  error?: { status: number; message: string };
}

interface OwnerDto {
  ID: string;
  Name?: string;
}

export interface EnsureOwnerItem {
  id?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class EntityOwnerService {
  private cache = new Map<string, string>();
  private ownersSubject = new BehaviorSubject<Map<string, string>>(new Map());
  owners$ = this.ownersSubject.asObservable();

  private readonly fallbackBase = this.normalizeBase(environment.apiUrl);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.authService.selectedDcr$
      .pipe(distinctUntilChanged())
      .subscribe(entityId => this.resetCache(entityId));
  }

  ensureOwners(items: EnsureOwnerItem[]): void {
    const unknownIds = this.collectUnknownIds(items);
    if (unknownIds.length === 0) return;

    const endpoint = this.composeEndpoint('/webapp/crud/publishers/names');
    this.http.post<GenericResponse<OwnerDto[]>>(
      endpoint.url,
      { IDs: unknownIds },
      { headers: this.buildHeaders(endpoint.useRemote) }
    ).pipe(
      map(res => res.payload ?? []),
      catchError(() => of<OwnerDto[]>([]))
    ).subscribe(owners => {
      owners.forEach(owner => this.cacheOwner(owner));
      this.ownersSubject.next(new Map(this.cache));
    });
  }

  private resetCache(entityId: string | null): void {
    this.cache.clear();
    if (entityId) {
      const name = this.authService.getDcrNameById(entityId);
      if (name && name !== entityId) {
        this.cache.set(`publisher::${entityId}`, name);
      }
    }
    this.ownersSubject.next(new Map(this.cache));
  }

  private collectUnknownIds(items: EnsureOwnerItem[]): string[] {
    const unknown = new Set<string>();
    for (const item of items) {
      const id = (item.id ?? '').toString().trim();
      if (id && !this.cache.has(`publisher::${id}`)) {
        unknown.add(id);
      }
    }
    return Array.from(unknown);
  }

  private cacheOwner(owner: OwnerDto): void {
    const id = (owner.ID ?? '').toString().trim();
    if (!id) return;
    const name = owner.Name?.trim() || id;
    this.cache.set(`publisher::${id}`, name);
  }

  private composeEndpoint(path: string): { url: string; useRemote: boolean } {
    const info = this.getBaseUrlInfo();
    return { url: `${info.baseUrl}${path.startsWith('/') ? path : '/' + path}`, useRemote: info.useRemote };
  }

  private buildHeaders(useRemote: boolean): HttpHeaders {
    if (useRemote) {
      const token = this.authService.getTncApiToken();
      let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      if (token) headers = headers.set('Authorization', token);
      return headers;
    }
    let headers = new HttpHeaders(this.authService.getAuthHeaders());
    return headers.set('Content-Type', 'application/json');
  }

  private getBaseUrlInfo(): { baseUrl: string; useRemote: boolean } {
    const remoteBase = this.authService.getTncApiBaseUrl();
    const remoteToken = this.authService.getTncApiToken();
    if (remoteBase && remoteToken) {
      return { baseUrl: this.normalizeBase(remoteBase), useRemote: true };
    }
    return { baseUrl: this.fallbackBase, useRemote: false };
  }

  private normalizeBase(url: string): string {
    return url ? url.replace(/\/+$/u, '') : '';
  }
}
